import React, { useState } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { Plus, Trash2, GripVertical, Eye, EyeOff, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import AdminConfirmDialog from '@/components/admin/AdminConfirmDialog';
import toast from 'react-hot-toast';
import type { FAQ, BilingualText } from '@/types';

const INPUT_CLS = "w-full px-4 py-3 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20";
const TEXTAREA_CLS = INPUT_CLS + " resize-none min-h-[80px]";
const LABEL_CLS = "block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-1.5";

const EMPTY_BIL: BilingualText = { en: '', ka: '' };

const AdminFAQPage: React.FC = () => {
  const faqs = useAdminStore((s) => s.faqs);
  const addFAQ = useAdminStore((s) => s.addFAQ);
  const updateFAQ = useAdminStore((s) => s.updateFAQ);
  const deleteFAQ = useAdminStore((s) => s.deleteFAQ);
  const reorderFAQs = useAdminStore((s) => s.reorderFAQs);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [lang, setLang] = useState<'en' | 'ka'>('en');

  const sorted = [...(faqs ?? [])].sort((a, b) => a.displayOrder - b.displayOrder);
  const orderedIds = sorted.map((f) => f._id);

  const handleAdd = (): void => {
    const newFaq = addFAQ({
      question: { en: '', ka: '' },
      answer: { en: '', ka: '' },
      displayOrder: sorted.length,
      isPublished: true,
    });
    setExpandedId(newFaq._id);
    toast.success('New FAQ item added');
  };

  const handleDelete = (): void => {
    if (!deleteId) return;
    deleteFAQ(deleteId);
    toast.success('FAQ item deleted');
    setDeleteId(null);
    if (expandedId === deleteId) setExpandedId(null);
  };

  const handleReorder = (newOrder: string[]): void => {
    reorderFAQs(newOrder);
  };

  const updateBilingual = (id: string, field: 'question' | 'answer', langKey: 'en' | 'ka', value: string): void => {
    const faq = sorted.find((f) => f._id === id);
    if (!faq) return;
    updateFAQ(id, {
      [field]: { ...faq[field], [langKey]: value },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">FAQ</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">
            {sorted.length} question{sorted.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Language toggle */}
          <div className="flex items-center bg-black/[0.03] dark:bg-white/[0.05] rounded-xl overflow-hidden border border-black/[0.06] dark:border-white/[0.08]">
            <button
              onClick={() => setLang('en')}
              className={`px-3.5 py-2 text-[10px] font-bold uppercase tracking-[0.1em] transition-all ${
                lang === 'en' ? 'bg-black dark:bg-white text-white dark:text-black' : 'text-black/40 dark:text-white/40'
              }`}
            >
              EN
            </button>
            <button
              onClick={() => setLang('ka')}
              className={`px-3.5 py-2 text-[10px] font-bold uppercase tracking-[0.1em] transition-all ${
                lang === 'ka' ? 'bg-black dark:bg-white text-white dark:text-black' : 'text-black/40 dark:text-white/40'
              }`}
            >
              KA
            </button>
          </div>
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 px-5 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity"
          >
            <Plus size={14} /> Add Question
          </button>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="text-center py-20">
          <HelpCircle size={32} className="mx-auto text-black/10 dark:text-white/10 mb-4" />
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">
            No FAQ items yet
          </p>
          <p className="text-[10px] text-black/15 dark:text-white/15 mt-1">
            Add your first question to get started
          </p>
        </div>
      ) : (
        <Reorder.Group
          axis="y"
          values={orderedIds}
          onReorder={handleReorder}
          className="space-y-3"
        >
          <AnimatePresence mode="popLayout">
            {sorted.map((faq) => {
              const isExpanded = expandedId === faq._id;
              return (
                <Reorder.Item
                  key={faq._id}
                  value={faq._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl overflow-hidden hover:border-black/10 dark:hover:border-white/10 transition-all"
                >
                  {/* Header row */}
                  <div className="flex items-center gap-3 px-4 py-3.5">
                    <GripVertical size={14} className="text-black/15 dark:text-white/15 cursor-grab active:cursor-grabbing shrink-0" />

                    <button
                      onClick={() => setExpandedId(isExpanded ? null : faq._id)}
                      className="flex-1 text-left min-w-0"
                    >
                      <p className="text-[13px] font-bold truncate">
                        {faq.question[lang] || faq.question.en || 'Untitled question'}
                      </p>
                      {!isExpanded && (faq.answer[lang] || faq.answer.en) && (
                        <p className="text-[10px] text-black/30 dark:text-white/30 truncate mt-0.5">
                          {faq.answer[lang] || faq.answer.en}
                        </p>
                      )}
                    </button>

                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => updateFAQ(faq._id, { isPublished: !faq.isPublished })}
                        className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
                          faq.isPublished
                            ? 'text-green-500 hover:bg-green-500/5'
                            : 'text-black/20 dark:text-white/20 hover:bg-black/[0.04] dark:hover:bg-white/[0.04]'
                        }`}
                        title={faq.isPublished ? 'Published' : 'Draft'}
                      >
                        {faq.isPublished ? <Eye size={13} /> : <EyeOff size={13} />}
                      </button>
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : faq._id)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-black/20 dark:text-white/20 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.04] transition-all"
                      >
                        {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                      </button>
                      <button
                        onClick={() => setDeleteId(faq._id)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-black/20 dark:text-white/20 hover:text-red-500 hover:bg-red-500/5 transition-all"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  {/* Expanded edit form */}
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                      className="border-t border-black/[0.04] dark:border-white/[0.04]"
                    >
                      <div className="p-5 space-y-5">
                        {/* Question */}
                        <div className="space-y-2">
                          <label className={LABEL_CLS}>Question</label>
                          <div className="relative">
                            <span className="absolute left-3.5 top-3 text-[8px] font-bold uppercase tracking-[0.15em] text-black/25">EN</span>
                            <input
                              value={faq.question.en}
                              onChange={(e) => updateBilingual(faq._id, 'question', 'en', e.target.value)}
                              placeholder="Enter question..."
                              className={INPUT_CLS + " pl-10"}
                            />
                          </div>
                          <div className="relative">
                            <span className="absolute left-3.5 top-3 text-[8px] font-bold uppercase tracking-[0.15em] text-black/25">KA</span>
                            <input
                              value={faq.question.ka}
                              onChange={(e) => updateBilingual(faq._id, 'question', 'ka', e.target.value)}
                              placeholder="შეიყვანეთ კითხვა..."
                              className={INPUT_CLS + " pl-10"}
                            />
                          </div>
                        </div>
                        {/* Answer */}
                        <div className="space-y-2">
                          <label className={LABEL_CLS}>Answer</label>
                          <div className="relative">
                            <span className="absolute left-3.5 top-3 text-[8px] font-bold uppercase tracking-[0.15em] text-black/25">EN</span>
                            <textarea
                              value={faq.answer.en}
                              onChange={(e) => updateBilingual(faq._id, 'answer', 'en', e.target.value)}
                              placeholder="Enter answer..."
                              className={TEXTAREA_CLS + " pl-10"}
                              rows={3}
                            />
                          </div>
                          <div className="relative">
                            <span className="absolute left-3.5 top-3 text-[8px] font-bold uppercase tracking-[0.15em] text-black/25">KA</span>
                            <textarea
                              value={faq.answer.ka}
                              onChange={(e) => updateBilingual(faq._id, 'answer', 'ka', e.target.value)}
                              placeholder="შეიყვანეთ პასუხი..."
                              className={TEXTAREA_CLS + " pl-10"}
                              rows={3}
                            />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </Reorder.Item>
              );
            })}
          </AnimatePresence>
        </Reorder.Group>
      )}

      <AdminConfirmDialog
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete FAQ"
        message="This will permanently remove this FAQ item. This action cannot be undone."
      />
    </div>
  );
};

export default AdminFAQPage;
